//@flow
import * as React from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import { compose } from 'recompose';
import { translate, type TFunction } from 'react-i18next';
import { Text, Flex, Box, Icon } from '@locus-taxy/neo-ui';
import classnames from 'classnames';
import { OutlineCheckbox } from 'common/components/core/form/Checkbox';
import {
    RadioGroup,
    RadioButton
} from 'common/components/core/form/RadioButton';
import theme from './TerritoryMapOptions.scss';
import { SizeEnum } from 'common/enums/UIEnums';
import { MarkerTypeEnum, TourClusterTypeEnum } from 'common/enums/MapEnum';
import { PolygonOptionTypes } from 'common/entity/pjpMaster/planView/FpAllocatedVisitsContainer';

class MapOptionsOutlineCheckbox extends React.PureComponent<$FlowFixMeProps> {
    onCheckboxChange = value => {
        this.props.onChange(this.props.mapOptionName, value);
    };
    render() {
        const { mapOptionName, ...others } = this.props; //eslint-disable-line no-unused-vars
        return (
            <OutlineCheckbox
                {...others}
                onChange={this.onCheckboxChange}
                onDoubleClick
            />
        );
    }
}
class MapOptionsRadioGroup extends React.PureComponent<$FlowFixMeProps> {
    onRadioGroupChange = value => {
        this.props.onChange(this.props.mapOptionName, value);
    };
    render() {
        const { 'data-test-id': dataTestId, ...others } = this.props;
        return (
            <Box data-test-id={dataTestId}>
                <RadioGroup {...others} onChange={this.onRadioGroupChange} />
            </Box>
        );
    }
}

type MarkerType = $Values<typeof MarkerTypeEnum>;

type OwnProps = {
    theme: ThemeType,
    showMarkers: boolean,
    allMarkers: boolean,
    markerType: MarkerType,
    showPolygonClustersFor: $Values<typeof TourClusterTypeEnum>,
    polygon: boolean,
    showUnassignedMarkers: boolean,
    currentUnallocatedCount?: number,
    onChange: (options: Object) => void
};

type Props = {
    ...OwnProps,
    theme: ThemeType,
    t: TFunction,
    mapOptions: {
        showUnassignedMarkers: boolean,
        showMarkers: boolean,
        polygon: boolean,
        polygonOption: string,
        allMarkers: boolean
    },
    mapControlVisible: boolean,
    setMapControlVisible: (visible: boolean) => void,
    polygonOption: $Values<typeof PolygonOptionTypes>
};
type State = {
    options: {
        showUnassignedMarkers: boolean,
        polygon: boolean,
        allMarkers: boolean,
        showMarkers: boolean
    }
};
class TerritoryMapOptions extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            options: {
                showUnassignedMarkers: false,
                showMarkers: false,
                allMarkers: true,
                polygon: false
            }
        };
        this.onToggleView = this.onToggleView.bind(this);
    }

    onValueChange = (id: string, value: string | boolean) => {
        const { mapOptions } = this.props;
        const { onChange } = this.props;
        const _mapOptions = Object.assign({}, mapOptions);
        _mapOptions[id] = value;
        onChange(_mapOptions);
    };

    onToggleView = () => {
        if (this.props.mapControlVisible) {
            this.hide();
        } else {
            this.show();
        }
    };
    show = () => {
        const { setMapControlVisible } = this.props;
        setMapControlVisible(true);
    };
    hide = () => {
        const { setMapControlVisible } = this.props;
        setMapControlVisible(false);
    };

    updateMapOptions = options => {
        this.setState(prevState => {
            const prevOptions = prevState.options;
            return {
                options: {
                    ...prevOptions,
                    ...options
                }
            };
        });
    };

    render() {
        const {
            theme,
            showMarkers,
            polygon,
            polygonOption,
            showUnassignedMarkers,
            mapControlVisible,
            allMarkers,
            t
        } = this.props;

        return (
            <Box
                className={classnames(theme.mapOptionsWrapRight, {
                    [theme.open]: mapControlVisible
                })}
            >
                <Flex
                    height={32}
                    alignItems="center"
                    px={2}
                    py={1}
                    onClick={this.onToggleView}
                    sx={{
                        cursor: 'pointer'
                    }}
                >
                    <Box width={12}>
                        <Icon
                            color="white"
                            icon={
                                mapControlVisible
                                    ? 'arrow_drop_down'
                                    : 'arrow_drop_right'
                            }
                        />
                    </Box>
                    <Box px={2}>
                        <Text fontSize={0} color="white">
                            {t('mapControls')}
                        </Text>
                    </Box>
                </Flex>
                <Box className={theme.checkboxContents}>
                    <MapOptionsOutlineCheckbox
                        label={t(
                            'entityManagement:createTerritory.unallocatedOutlets'
                        )}
                        checked={showUnassignedMarkers}
                        onChange={this.onValueChange}
                        mapOptionName="showUnassignedMarkers"
                        theme={theme}
                    />
                    <MapOptionsOutlineCheckbox
                        checked={showMarkers}
                        onChange={this.onValueChange}
                        mapOptionName="showMarkers"
                        theme={theme}
                        label={t('showMarkers')}
                    />
                    <MapOptionsOutlineCheckbox
                        checked={allMarkers}
                        onChange={this.onValueChange}
                        mapOptionName="allMarkers"
                        theme={theme}
                        label={t('entityManagement:fpPlanView.allMarkers')}
                    />
                    <MapOptionsOutlineCheckbox
                        checked={polygon}
                        onChange={this.onValueChange}
                        mapOptionName="polygon"
                        theme={theme}
                        label={t('polygon').toUpperCase()}
                    />
                    <MapOptionsRadioGroup
                        onChange={this.onValueChange}
                        mapOptionName="polygonOption"
                        value={polygon ? polygonOption : null}
                        theme={theme}
                    >
                        <RadioButton
                            theme={theme}
                            label={t('entityManagement:fpPlanView.all')}
                            value={PolygonOptionTypes.ALL}
                            disabled={!polygon}
                            invert
                            size={SizeEnum.Large}
                        />
                        <RadioButton
                            theme={theme}
                            label={t('entityManagement:fpPlanView.selected')}
                            value={PolygonOptionTypes.SELECTED}
                            disabled={!polygon}
                            invert
                            size={SizeEnum.Large}
                        />
                    </MapOptionsRadioGroup>
                </Box>
            </Box>
        );
    }
}

export default (compose(
    themr('TerritoryMapOptions', theme, { withRef: true }),
    translate('planner', { withRef: true })
)(TerritoryMapOptions): React.AbstractComponent<OwnProps>);
